import React, { useState, useMemo } from 'react';
import { ArrowUpRight, Clock, Lock } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import SubscriptionReportSection from '../components/SubscriptionReportSection';
import ReportToolbar from '../components/ReportToolbar';

// ——— FEATURED ———
const featured = [];

// ——— ALL MODULES ———
const modules = [];

const CATEGORIES = [
  'All',
  'AI for Finance Professionals',
  'Corporate Governance',
  'Corporate Law Intelligence',
  'Cost and Accounting Management Intelligence',
  'Risk Management',
  'Tax Intelligence'
];
const LEVELS = [
  'AI Foundations',
  'Advanced',
  'Basic',
  'Corporate Law',
  'Costing',
  'Direct Tax',
  'Enterprise Risk',
  'Executive AI',
  'Finance Functions',
  'Governance',
  'Indirect Tax',
  'Planning and Control',
  'Strategic Cost Management',
  'Transactions'
];

const signals = [];

const tierStyle = (t) => ({
  CORE: 'bg-purple-50 text-purple-700 border-purple-200',
  ADVANCED: 'bg-amber-50 text-amber-700 border-amber-200',
  FOUNDATION: 'bg-emerald-50 text-emerald-700 border-emerald-200',
}[t] || 'bg-slate-50 text-slate-700 border-slate-200');

const statusStyle = (s) =>
  s === 'Published' ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700';

// ——— FEATURED CARD ———
const FeaturedCard = ({ item }) => (
  <a href={`#learning-${item.id}`} className="group relative bg-white border border-slate-200 rounded-2xl p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(13,27,42,0.14)] hover:border-[#C8A84B]/50 overflow-hidden" data-testid={`learning-featured-${item.id}`}>
    <div className="absolute top-0 right-0 w-12 h-12 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
      <div className="absolute top-3 right-3 w-6 h-px bg-[#C8A84B]" />
      <div className="absolute top-3 right-3 w-px h-6 bg-[#C8A84B]" />
    </div>
    <div className="flex items-start gap-4">
      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${item.accent === 'purple' ? 'bg-purple-50 text-purple-600' : 'bg-amber-50 text-[#C8A84B]'}`}>
        {item.icon}
      </div>
      <div className="flex-1">
        <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#C8A84B] font-semibold mb-2">{item.tag}</p>
        <h3 className="text-2xl md:text-3xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-tight mb-3">{item.title}</h3>
        <p className="text-slate-600 leading-relaxed italic border-l-2 border-[#C8A84B]/40 pl-4">{item.summary}</p>
      </div>
    </div>
  </a>
);

// ——— GRID CARD ———
const GridCard = ({ m }) => (
  <a href={`#learning-${m.id}`} className="group relative bg-white border border-slate-200 rounded-2xl p-7 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(13,27,42,0.14)] hover:border-[#C8A84B]/50 overflow-hidden flex flex-col" data-testid={`learning-card-${m.id}`}>
    <div className="absolute top-0 right-0 w-12 h-12 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
      <div className="absolute top-3 right-3 w-6 h-px bg-[#C8A84B]" />
      <div className="absolute top-3 right-3 w-px h-6 bg-[#C8A84B]" />
    </div>

    <div className="flex items-center justify-between mb-5">
      <span className={`inline-flex items-center px-2.5 py-1 text-[10px] font-mono font-bold tracking-[0.15em] uppercase rounded ${statusStyle(m.status)}`}>{m.status}</span>
      <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-600">{m.icon}</div>
    </div>

    <div className="mb-3">
      <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-1">
        {m.category} <span className="text-slate-300 mx-1">·</span> {m.level}
      </p>
      <h3 className="text-2xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-tight">{m.title}</h3>
    </div>

    <p className="text-[15px] text-slate-700 leading-snug font-medium italic border-l-2 border-[#C8A84B]/40 pl-4 mb-6">{m.subtitle}</p>

    <div className="mb-4">
      <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-semibold rounded border ${tierStyle(m.tier)}`}>{m.tier}</span>
    </div>

    <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-100">
      <span className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400 tracking-widest uppercase">
        <Clock className="w-3 h-3" /> {m.duration}
      </span>
      <div className="flex items-center gap-1.5 text-sm font-medium text-[#C8A84B] group-hover:gap-3 transition-all duration-300">
        <span className="font-mono text-xs tracking-wider uppercase">Start</span>
        <ArrowUpRight className="w-4 h-4" />
      </div>
    </div>
  </a>
);

// ——— LIST ROW ———
const ListRow = ({ m }) => (
  <a href={`#learning-${m.id}`} className="group flex items-center gap-6 bg-white border border-slate-200 rounded-xl px-6 py-5 transition-all duration-300 hover:border-[#C8A84B]/50 hover:shadow-md" data-testid={`learning-row-${m.id}`}>
    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-600">{m.icon}</div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className={`inline-flex items-center px-2 py-0.5 text-[9px] font-mono font-bold tracking-[0.15em] uppercase rounded ${statusStyle(m.status)}`}>{m.status}</span>
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded border ${tierStyle(m.tier)}`}>{m.tier}</span>
        <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">{m.category} · {m.level}</span>
      </div>
      <h3 className="text-lg font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-tight mb-1">{m.title}</h3>
      <p className="text-sm text-slate-600 leading-snug line-clamp-1">{m.subtitle}</p>
    </div>
    <div className="hidden md:flex flex-col items-end gap-1 text-right flex-shrink-0">
      <span className="text-[10px] font-mono text-slate-400 tracking-widest uppercase">{m.date}</span>
      <span className="text-[10px] font-mono text-slate-400 tracking-widest uppercase">{m.duration}</span>
    </div>
    <ArrowUpRight className="flex-shrink-0 w-5 h-5 text-[#C8A84B] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
  </a>
);

// ——— PAGE ———
const Learning = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [level, setLevel] = useState('All Levels');
  const [viewMode, setViewMode] = useState('grid');
  const [uploadedReportCount, setUploadedReportCount] = useState(0);

  const filtered = useMemo(() => {
    return modules.filter((m) => {
      const c = cat === 'All' || m.category === cat;
      const l = level === 'All Levels' || m.level === level;
      const t = !q.trim() || [m.title, m.subtitle, m.category].join(' ').toLowerCase().includes(q.toLowerCase());
      return c && l && t;
    });
  }, [q, cat, level]);

  return (
    <>
      <PageMeta
        title="Professional Intelligence | The B Tree"
        description="Build stronger finance, governance, risk, valuation, and AI decision frameworks with The B Tree Professional Intelligence."
        canonical="https://www.thebtree.com/professional-intelligence"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="learning-page">
      {/* HEADER */}
      <div className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
          <div className="flex items-start justify-between mb-6 gap-6">
            <div>
              <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">CA • CMA • CS • CFA • FRM • ACCA • CPA • FINANCE PROFESSIONALS</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-[1.05] mb-3">Professional Intelligence™</h1>
              <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
                Build the judgement, strategic thinking and practical expertise required in modern finance. Explore intelligence across financial analysis, valuation, corporate finance, governance, risk, taxation, corporate law, cost management and AI.
              </p>
            </div>
          </div>

        </div>
      </div>

      <ReportToolbar
        searchValue={q}
        onSearchChange={setQ}
        searchPlaceholder="Search Financial Statements, Valuation, Audit, Corporate Finance, Tax, Risk, AI..."
        reportCount={uploadedReportCount}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        testId="learning"
        filterContent={(
          <>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 min-w-[180px] max-w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 cursor-pointer">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
            </select>
            <select value={level} onChange={(e) => setLevel(e.target.value)} className="h-10 min-w-[160px] max-w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 cursor-pointer">
              <option>All Levels</option>
              {LEVELS.map((l) => <option key={l}>{l}</option>)}
            </select>
          </>
        )}
      />

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
          <div className="mb-6">
            <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] mb-2 font-semibold">Editor's Picks</p>
            <h2 className="text-3xl md:text-4xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A]">Featured Masterclasses</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featured.map((f) => <FeaturedCard key={f.id} item={f} />)}
          </div>
        </section>
      )}

      {/* RESULTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <SubscriptionReportSection
          category="Learning"
          title="Uploaded Professional Intelligence™ Reports"
          onCountChange={setUploadedReportCount}
          viewMode={viewMode}
          viewMode={viewMode}
          filters={{
            query: q,
            primary: cat,
            secondary: level,
            primaryAllValues: ['All'],
            secondaryAllValues: ['All Levels'],
          }}
        />

        {modules.length > 0 && (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-3'} data-testid="learning-results">
            {filtered.map((m) => viewMode === 'grid' ? <GridCard key={m.id} m={m} /> : <ListRow key={m.id} m={m} />)}
          </div>
        )}
      </section>

      {signals.length > 0 && (
        <section className="bg-[#F5F1E8] border-y border-[#C8A84B]/20 py-16" data-testid="learning-signals">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] mb-3 font-semibold text-center">Study Guide</p>
            <h2 className="text-3xl md:text-4xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] text-center mb-10">Three Rules of Institutional Modeling</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {signals.map((s) => (
                <div key={s.n} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-[#C8A84B]/40 transition-colors">
                  <div className="flex items-start gap-4">
                    <span className="font-[Cormorant_Garamond] font-medium text-4xl text-[#C8A84B] leading-none">#{s.n}</span>
                    <p className="text-[#0D1B2A] leading-relaxed font-medium">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C8A84B]/10 text-[#C8A84B] mb-6">
          <Lock className="w-6 h-6" />
        </div>
        <h2 className="text-3xl md:text-5xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-4">Unlock the full curriculum</h2>
        <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-8">Every module, every Excel template, every practitioner walkthrough — on demand.</p>
        <button
          className="inline-flex items-center gap-3 bg-[#0D1B2A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#1A2B3F] transition-all duration-300 hover:gap-4 active:scale-[0.98]"
          data-testid="learning-unlock-button"
          onClick={() => window.location.assign('/signup')}
        >
          Enroll Now <ArrowUpRight className="w-4 h-4" />
        </button>
      </section>
    </div>
    </>
  );
};

export default Learning;
